import { IConfirmDialogProps } from 'Components/Core';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { GridColWidth } from '../../../Layout/Core/getColWidth';

/**
 * Used for properties that need to vary record-to-record, represented with a function that takes the index of the record.
 */
export type IndexVariable<T> = (i: number) => T;

/**
 * Defines a field for a record grid
 */
export type RecordGridField = {
  /**
   * The name of the field (relative to the record's root)
   */
  name: string;
  /**
   * The component to display within the cell for this field
   *
   * When this is index variable, it is determined by the index of the record.
   *
   * Does not need to include name property as this is applied to it afterwards
   */
  component: JSX.Element | IndexVariable<JSX.Element>;
  /**
   * The label of the field. If not set, the configured label for the field name will be used.
   */
  label?: string | IndexVariable<string>;
  /**
   * If true, this field will be hidden.
   */
  hidden?: boolean;
  /**
   * The additional message paths property of the field component
   *
   * TODO: We are defining this here and not on the component only because we need to apply record prefixes to it.
   * This should be done with a NamePrefixModifier - but some grid behaviour
   * (such as tabbing) breaks when the field is not a direct child of the grid.
   */
  additionalMessagePaths?: Array<string>;

  /**
   * Defines the width of field column (i.e. when orientation is vertical)
   */
  width?: GridColWidth;
  /**
   * Stores the index of subRecord
   */
  fieldSubRecordIndex?: number;
};

/**
 * Properties that define how records are displayed and managed by this grid
 */
export type RecordProps = {
  /**
   * Defines when to prevent removal of records
   */
  preventRemove?: 'all-but-last' | boolean | IndexVariable<boolean>;
  /**
   * Defines when to dim (shade) a record
   */
  dim?: IndexVariable<boolean>;
  /**
   * Defines if records can be created
   */
  preventCreate?: boolean | IndexVariable<boolean>;
  /**
   * Defines when (and how) user is prompt to confirm the removal of records
   */
  removeConfirm?: IConfirmDialogProps;
  /**
   * When true, we will always show fields for the first record - even if the record set is currently empty.
   * Note entering a value in a field will create the record.
   *
   * TODO: There is a subtle bug where "create" needs to be triggered twice if the first record is shown but doesn't exist
   */
  enforceAtLeastOneRecord?: boolean;
  /**
   * A heading for each record.
   * Set to 'number' to enumerate records as 1, 2, 3, etc.
   */
  heading?: 'number' | IndexVariable<string | undefined>;
  /**
   * The label to show next to the record headings
   */
  headingLabel?: string;

  /**
   * Defines the size of groups records can be managed in (e.g. if set to 2, records can only be added and removed in pairs)
   */
  recordGroupingSize?: number;

  /**
   * Defines the width of record column (i.e. when orientation is horizontal)
   */
  width?: GridColWidth;
};

/**
 * A set of fields to be displayed for each record in a grid
 *
 * Can include sets of fields for subrecords
 **/
export type RecordGridFieldSet = Array<RecordGridField | SubRecordGridFieldSet>;

/**
 * A set of fields to be displayed for each sub-record in a grid
 *
 * Note this cannot include further subrecord sets.
 **/
export type SubRecordGridFieldSet = {
  /**
   * The name of the property for the sub record collection
   */
  name: string;
  /**
   * The fields within this field set.
   */
  fields: Array<RecordGridField>;
  /**
   * Properties that define how sub-records are displayed and managed by this grid
   *
   * Can be index-variable (based on the index of the owner record)
   */
  recordProps?: SubRecordProps | IndexVariable<SubRecordProps>;
  /**
   * If true, this field set will be hidden.
   */
  hidden?: boolean;
};

/**
 * Properties that define how sub-records are displayed and managed by this grid
 *
 * Essentially {@link RecordProps} but we only pick out the supported properties
 */
export type SubRecordProps = Pick<
  RecordProps,
  'preventCreate' | 'preventRemove' | 'removeConfirm' | 'enforceAtLeastOneRecord' | 'heading' | 'recordGroupingSize'
>;

export const isSubRecordFieldSet = (field: RecordGridField | SubRecordGridFieldSet): field is SubRecordGridFieldSet =>
  hasNonEmptyValue((field as SubRecordGridFieldSet)?.fields) && hasValue((field as SubRecordGridFieldSet)?.name);

export const isIndexVariable = <T>(field: T | IndexVariable<T>): field is IndexVariable<T> =>
  typeof field === 'function';
